import { useState, useEffect } from "react";
import { voteStore } from "../utils/dataStore";
import type { Vote } from "../../../api/types";
import { apiDelete, apiPost, apiPut } from "../../../api/http";
import { voteToUpsertBody } from "../../../api/loadDataStore";
import { useAdminData } from "../AdminDataContext";
import { useMemberAudienceUi } from "../hooks/useMemberAudienceUi";
import VoteCreateModal from "./VoteCreateModal";
import VoteReportModal from "./VoteReportModal";
import { reportError } from "../../../api/errors";

const statusConfig = {
  active: { label: "进行中", color: "bg-green-100 text-green-700" },
  ended:  { label: "已结束", color: "bg-gray-100 text-gray-500" },
  draft:  { label: "草稿",   color: "bg-amber-100 text-amber-700" },
};

const targetScopeLabel = (vote: Vote): string | null => {
  const s = vote.targetScope;
  if (!s || s.type === "all") return null;
  if (s.type === "level_normal") return "普通会员";
  if (s.type === "level_premium") return "高级会员";
  if (s.type === "level_director") return "理事会员";
  if (s.type === "activity_enrolled") return `已报名「${s.activityTitle ?? "活动"}」`;
  if (s.type === "activity_not_enrolled") return `未报名「${s.activityTitle ?? "活动"}」`;
  return null;
};

export default function VoteManager() {
  const { tenantLegacyId, reload, storeVersion } = useAdminData();
  const { audience, loading: audienceLoading } = useMemberAudienceUi();
  const [votes, setVotes] = useState<Vote[]>(() => voteStore.getAll());
  const [selectedVote, setSelectedVote] = useState<Vote | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editVote, setEditVote] = useState<Vote | null>(null);
  const [reportVote, setReportVote] = useState<Vote | null>(null);
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "ended" | "draft">("all");
  const [showMemberPreview, setShowMemberPreview] = useState<Vote | null>(null);
  const [previewSelected, setPreviewSelected] = useState<string[]>([]);
  const [detailTab, setDetailTab] = useState<"results" | "participants">("results");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [scheduleVoteId, setScheduleVoteId] = useState<string | null>(null);
  const [scheduleTime, setScheduleTime] = useState("");

  useEffect(() => {
    const list = voteStore.getAll();
    setVotes(list);
    setSelectedVote((cur) => {
      if (!cur) return null;
      const fresh = list.find((v) => v.id === cur.id);
      return fresh ?? cur;
    });
  }, [storeVersion]);

  /* ── Auto-publish scheduled votes ── */
  useEffect(() => {
    const checkScheduled = async () => {
      if (!tenantLegacyId) return;
      const list = voteStore.getAll();
      const due = list.filter(
        (v) =>
          v.status === "draft" &&
          v.scheduledPublishAt &&
          new Date(v.scheduledPublishAt) <= new Date(),
      );
      if (due.length === 0) return;
      try {
        await Promise.all(
          due.map((v) =>
            apiPut(
              `/api/tenants/${tenantLegacyId}/votes/${encodeURIComponent(v.id)}`,
              voteToUpsertBody({ ...v, status: "active", scheduledPublishAt: undefined, showToMembers: true }),
            ),
          ),
        );
        await reload();
      } catch {
        /* ignore timer failures */
      }
    };
    void checkScheduled();
    const timer = setInterval(() => void checkScheduled(), 30000);
    return () => clearInterval(timer);
  }, [tenantLegacyId, reload]);

  const filtered = filterStatus === "all" ? votes : votes.filter((v) => v.status === filterStatus);
  const totalVotes = (v: Vote) => v.options.reduce((s, o) => s + o.votes, 0);

  const handleCreate = async (vote: Vote) => {
    try {
      await apiPost(`/api/tenants/${tenantLegacyId}/votes`, voteToUpsertBody(vote));
      await reload();
      setShowCreate(false);
    } catch (e) {
      reportError(e, "创建失败");
    }
  };

  const handleEditSave = async (vote: Vote) => {
    try {
      await apiPut(`/api/tenants/${tenantLegacyId}/votes/${encodeURIComponent(vote.id)}`, voteToUpsertBody(vote));
      await reload();
      if (selectedVote?.id === vote.id) setSelectedVote(vote);
      setEditVote(null);
    } catch (e) {
      reportError(e, "保存失败");
    }
  };

  const handleToggleShowToMembers = async (id: string) => {
    const current = votes.find((v) => v.id === id);
    if (!current) return;
    const next = { ...current, showToMembers: !current.showToMembers };
    try {
      await apiPut(`/api/tenants/${tenantLegacyId}/votes/${encodeURIComponent(id)}`, voteToUpsertBody(next));
      await reload();
    } catch (e) {
      reportError(e, "更新失败");
    }
  };

  const handlePublish = async (id: string) => {
    const v = votes.find((x) => x.id === id);
    if (!v) return;
    try {
      await apiPut(
        `/api/tenants/${tenantLegacyId}/votes/${encodeURIComponent(id)}`,
        voteToUpsertBody({ ...v, status: "active", showToMembers: true }),
      );
      await reload();
    } catch (e) {
      reportError(e, "发布失败");
    }
  };

  const handleEnd = async (id: string) => {
    const v = votes.find((x) => x.id === id);
    if (!v) return;
    try {
      await apiPut(
        `/api/tenants/${tenantLegacyId}/votes/${encodeURIComponent(id)}`,
        voteToUpsertBody({ ...v, status: "ended" }),
      );
      await reload();
    } catch (e) {
      reportError(e, "操作失败");
    }
  };

  const handleDeleteConfirmed = async (id: string) => {
    const vote = votes.find((v) => v.id === id);
    if (!vote || vote.status !== "draft") return;
    try {
      await apiDelete(`/api/tenants/${tenantLegacyId}/votes/${encodeURIComponent(id)}`);
      await reload();
      if (selectedVote?.id === id) setSelectedVote(null);
      setDeleteConfirmId(null);
    } catch (e) {
      reportError(e, "删除失败");
    }
  };

  const handleSelectVote = (vote: Vote) => {
    setSelectedVote(vote);
    setDetailTab("results");
  };

  const handleDuplicate = async (vote: Vote) => {
    const newVote: Vote = {
      ...vote,
      id: `v${Date.now()}`,
      title: `${vote.title}（副本）`,
      status: "draft",
      totalVoters: 0,
      showToMembers: false,
      startDate: vote.startDate,
      endDate: vote.endDate,
      options: vote.options.map((o) => ({ ...o, votes: 0 })),
      participants: [],
    };
    try {
      await apiPost(`/api/tenants/${tenantLegacyId}/votes`, voteToUpsertBody(newVote));
      await reload();
    } catch (e) {
      reportError(e, "复制失败");
    }
  };

  const handleSchedule = async (id: string, time: string) => {
    const v = votes.find((x) => x.id === id);
    if (!v) return;
    const iso = time.includes("T") ? new Date(time).toISOString() : new Date(`${time}:00.000Z`).toISOString();
    try {
      await apiPut(
        `/api/tenants/${tenantLegacyId}/votes/${encodeURIComponent(id)}`,
        voteToUpsertBody({ ...v, scheduledPublishAt: iso }),
      );
      await reload();
    } catch (e) {
      reportError(e, "定时失败");
    }
    setScheduleVoteId(null);
    setScheduleTime("");
  };

  const handleCancelSchedule = async (id: string) => {
    const v = votes.find((x) => x.id === id);
    if (!v) return;
    try {
      await apiPut(
        `/api/tenants/${tenantLegacyId}/votes/${encodeURIComponent(id)}`,
        voteToUpsertBody({ ...v, scheduledPublishAt: undefined }),
      );
      await reload();
    } catch (e) {
      reportError(e, "取消失败");
    }
  };

  const handleExportParticipants = (vote: Vote) => {
    if (!vote.participants?.length) return;
    const optionMap = Object.fromEntries(vote.options.map((o) => [o.id, o.text]));
    const header = "姓名,会员ID,投票选项,投票时间";
    const rows = vote.participants.map((p) => {
      const options = p.votedOptions.map((oid) => optionMap[oid] ?? oid).join("；");
      return `${p.name},${p.memberId},"${options}",${p.time}`;
    });
    const bom = "\uFEFF";
    const csv = bom + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${vote.title}_参与名单_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex gap-6 h-full">
      {/* ── Left: Vote List ── */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-gray-800">投票管理</h2>
            <p className="text-xs text-gray-400 mt-0.5">创建和管理协会投票活动</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-green-50 px-3 py-1.5 rounded-xl">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
              <span>{votes.filter((v) => v.showToMembers && v.status === "active").length} 个投票对会员展示中</span>
            </div>
            {votes.filter((v) => v.scheduledPublishAt).length > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-xl">
                <i className="ri-alarm-line"></i>
                <span>{votes.filter((v) => v.scheduledPublishAt).length} 个定时待发布</span>
              </div>
            )}
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i> 新建投票
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-xl w-fit">
          {(["all", "active", "ended", "draft"] as const).map((s) => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-4 py-1.5 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap ${filterStatus === s ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              {s === "all" ? "全部" : statusConfig[s].label}
              {s !== "all" && <span className="ml-1.5 text-xs text-gray-400">{votes.filter((v) => v.status === s).length}</span>}
            </button>
          ))}
        </div>

        {/* Vote Cards */}
        <div className="space-y-3">
          {filtered.map((vote) => {
            const total = totalVotes(vote);
            const leading = vote.options.reduce((a, b) => (a.votes >= b.votes ? a : b), vote.options[0]);
            const hasImages = vote.options.some((o) => o.imageUrl);
            const scopeLabel = targetScopeLabel(vote);
            return (
              <div key={vote.id} onClick={() => handleSelectVote(vote)}
                className={`bg-white rounded-2xl p-5 cursor-pointer transition-all border-2 ${selectedVote?.id === vote.id ? "border-red-500" : "border-transparent hover:border-gray-200"}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusConfig[vote.status].color}`}>{statusConfig[vote.status].label}</span>
                      {vote.scheduledPublishAt && vote.status === "draft" && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 flex items-center gap-1">
                          <i className="ri-alarm-line text-xs"></i>
                          定时 {vote.scheduledPublishAt.replace("T", " ")}
                        </span>
                      )}
                      {vote.isAnonymous && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">匿名投票</span>}
                      {vote.allowMultiple && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">多选</span>}
                      {hasImages && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 flex items-center gap-1">
                          <i className="ri-image-line text-xs"></i> 图文选项
                        </span>
                      )}
                      {scopeLabel && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-600 flex items-center gap-1">
                          <i className="ri-focus-3-line text-xs"></i> {scopeLabel}
                        </span>
                      )}
                      {vote.showToMembers && vote.status === "active" && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span> 展示中
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 truncate">{vote.title}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">{vote.startDate} ~ {vote.endDate}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-2xl font-bold text-red-600">{vote.totalVoters}</p>
                    <p className="text-xs text-gray-400">参与人数</p>
                  </div>
                </div>

                {/* Option preview */}
                {vote.status !== "draft" && total > 0 && (
                  hasImages ? (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {vote.options.slice(0, 4).map((opt) => {
                        const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
                        return (
                          <div key={opt.id} className="flex-shrink-0 w-24 bg-gray-50 rounded-xl overflow-hidden">
                            {opt.imageUrl ? (
                              <div className="h-14 overflow-hidden"><img src={opt.imageUrl} alt={opt.text} className="w-full h-full object-cover" /></div>
                            ) : (
                              <div className="h-14 bg-gray-100 flex items-center justify-center"><i className="ri-image-line text-gray-300"></i></div>
                            )}
                            <div className="px-2 py-1.5">
                              <p className="text-xs text-gray-700 truncate font-medium leading-tight">{opt.text}</p>
                              <div className="flex items-center justify-between mt-1">
                                <div className="h-1 flex-1 bg-gray-200 rounded-full overflow-hidden mr-1">
                                  <div className={`h-full rounded-full ${opt.id === leading.id ? "bg-red-500" : "bg-gray-300"}`} style={{ width: `${pct}%` }}></div>
                                </div>
                                <span className="text-xs text-gray-400 flex-shrink-0">{pct}%</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {vote.options.slice(0, 3).map((opt) => (
                        <div key={opt.id}>
                          <div className="flex items-center justify-between text-xs mb-0.5">
                            <span className={`truncate max-w-[70%] ${opt.id === leading.id ? "font-medium text-gray-700" : "text-gray-500"}`}>{opt.text}</span>
                            <span className="text-gray-400 ml-2 flex-shrink-0">{total > 0 ? Math.round((opt.votes / total) * 100) : 0}%</span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${opt.id === leading.id ? "bg-red-500" : "bg-gray-300"}`} style={{ width: `${total > 0 ? (opt.votes / total) * 100 : 0}%` }}></div>
                          </div>
                        </div>
                      ))}
                      {vote.options.length > 3 && <p className="text-xs text-gray-400">+{vote.options.length - 3} 个选项...</p>}
                    </div>
                  )
                )}

                {/* Action buttons */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-50 flex-wrap">
                  {vote.status === "draft" && (
                    <button onClick={(e) => { e.stopPropagation(); handlePublish(vote.id); }} className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 cursor-pointer whitespace-nowrap transition-colors">
                      <i className="ri-send-plane-line"></i> 立即发布
                    </button>
                  )}
                  {/* Schedule / cancel schedule button */}
                  {vote.status === "draft" && (
                    vote.scheduledPublishAt ? (
                      <button onClick={(e) => { e.stopPropagation(); handleCancelSchedule(vote.id); }} className="flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-600 text-xs rounded-lg hover:bg-amber-100 cursor-pointer whitespace-nowrap transition-colors border border-amber-200">
                        <i className="ri-alarm-line"></i> 取消定时
                      </button>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); setScheduleVoteId(vote.id); setScheduleTime(""); }} className="flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-600 text-xs rounded-lg hover:bg-amber-100 cursor-pointer whitespace-nowrap transition-colors">
                        <i className="ri-time-line"></i> 定时发布
                      </button>
                    )
                  )}
                  {vote.status === "active" && (
                    <button onClick={(e) => { e.stopPropagation(); handleEnd(vote.id); }} className="flex items-center gap-1 px-3 py-1 bg-amber-500 text-white text-xs rounded-lg hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-colors">
                      <i className="ri-stop-circle-line"></i> 结束投票
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleToggleShowToMembers(vote.id); }}
                    className={`flex items-center gap-1.5 px-3 py-1 text-xs rounded-lg cursor-pointer whitespace-nowrap transition-all ${vote.showToMembers ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                  >
                    <i className={vote.showToMembers ? "ri-eye-line" : "ri-eye-off-line"}></i>
                    {vote.showToMembers ? "会员可见" : "仅内部"}
                  </button>
                  {vote.status === "active" && (
                    <button onClick={(e) => { e.stopPropagation(); setPreviewSelected([]); setShowMemberPreview(vote); }} className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap transition-colors">
                      <i className="ri-smartphone-line"></i> 会员视角
                    </button>
                  )}
                  {/* Edit button: draft only */}
                  {vote.status === "draft" && (
                    <button onClick={(e) => { e.stopPropagation(); setEditVote(vote); }} className="flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-600 text-xs rounded-lg hover:bg-amber-100 cursor-pointer whitespace-nowrap transition-colors">
                      <i className="ri-edit-line"></i> 编辑
                    </button>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); handleSelectVote(vote); }} className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap transition-colors">
                    <i className="ri-bar-chart-line"></i> 查看详情
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDuplicate(vote); }} className="flex items-center gap-1 px-3 py-1 bg-indigo-50 text-indigo-600 text-xs rounded-lg hover:bg-indigo-100 cursor-pointer whitespace-nowrap transition-colors" title="复制为新草稿">
                    <i className="ri-file-copy-line"></i> 复制
                  </button>
                  {/* Delete: draft only */}
                  {vote.status === "draft" ? (
                    <button onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(vote.id); }} className="flex items-center gap-1 px-3 py-1 bg-red-50 text-red-500 text-xs rounded-lg hover:bg-red-100 cursor-pointer whitespace-nowrap transition-colors ml-auto">
                      <i className="ri-delete-bin-line"></i> 删除
                    </button>
                  ) : (
                    <span className="ml-auto flex items-center gap-1 px-3 py-1 bg-gray-50 text-gray-300 text-xs rounded-lg whitespace-nowrap cursor-not-allowed" title="仅草稿状态可删除">
                      <i className="ri-lock-line"></i> 不可删除
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-survey-line text-4xl text-gray-200"></i></div>
              <p className="text-sm">暂无投票</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Right: Detail Panel ── */}
      {selectedVote && (
        <div className="w-80 flex-shrink-0">
          <div className="bg-white rounded-2xl p-5 sticky top-0 max-h-[calc(100vh-6rem)] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800 text-sm">投票详情</h3>
              <button onClick={() => setSelectedVote(null)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"><i className="ri-close-line"></i></button>
            </div>

            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusConfig[selectedVote.status].color}`}>{statusConfig[selectedVote.status].label}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 cursor-pointer ${selectedVote.showToMembers ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`} onClick={() => handleToggleShowToMembers(selectedVote.id)}>
                <i className={selectedVote.showToMembers ? "ri-eye-line" : "ri-eye-off-line"}></i>
                {selectedVote.showToMembers ? "会员可见" : "仅内部"}
              </span>
            </div>

            <h4 className="font-semibold text-gray-800 mb-1">{selectedVote.title}</h4>
            {selectedVote.description && <p className="text-xs text-gray-500 mb-3 leading-relaxed">{selectedVote.description}</p>}

            {/* Scheduled publish info */}
            {selectedVote.status === "draft" && selectedVote.scheduledPublishAt && (
              <div className="flex items-center justify-between mb-3 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <i className="ri-alarm-line text-amber-500 text-sm"></i>
                  <div>
                    <p className="text-xs font-semibold text-amber-700">定时发布</p>
                    <p className="text-xs text-amber-600">{selectedVote.scheduledPublishAt.replace("T", " ")}</p>
                  </div>
                </div>
                <button onClick={() => handleCancelSchedule(selectedVote.id)} className="text-xs text-amber-600 hover:text-amber-800 cursor-pointer px-2 py-1 hover:bg-amber-100 rounded-lg transition-colors whitespace-nowrap">取消</button>
              </div>
            )}

            {/* Draft without schedule: show quick schedule button */}
            {selectedVote.status === "draft" && !selectedVote.scheduledPublishAt && (
              <button onClick={() => { setScheduleVoteId(selectedVote.id); setScheduleTime(""); }} className="w-full flex items-center justify-center gap-2 mb-3 px-3 py-2.5 bg-amber-50 text-amber-700 text-xs rounded-xl hover:bg-amber-100 cursor-pointer transition-colors border border-amber-100 whitespace-nowrap">
                <i className="ri-time-line"></i> 设置定时发布时间
              </button>
            )}

            {/* Target scope display */}
            {targetScopeLabel(selectedVote) && (
              <div className="flex items-center gap-2 mb-3 bg-teal-50 rounded-xl px-3 py-2">
                <i className="ri-focus-3-line text-teal-500 text-xs"></i>
                <span className="text-xs text-teal-700">参与范围：{targetScopeLabel(selectedVote)}</span>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl mb-4">
              <div>
                <p className="text-xs font-medium text-gray-700">向会员端展示投票</p>
                <p className="text-xs text-gray-400 mt-0.5">开启后会员可在前台参与此投票</p>
              </div>
              <button onClick={() => handleToggleShowToMembers(selectedVote.id)} className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${selectedVote.showToMembers ? "bg-green-500" : "bg-gray-300"}`}>
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${selectedVote.showToMembers ? "left-6" : "left-1"}`}></span>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { label: "参与人数", value: selectedVote.totalVoters },
                { label: "选项数量", value: selectedVote.options.length },
                { label: "开始时间", value: selectedVote.startDate },
                { label: "结束时间", value: selectedVote.endDate },
              ].map((item) => (
                <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-0.5">{item.label}</p>
                  <p className="text-sm font-semibold text-gray-700">{item.value}</p>
                </div>
              ))}
            </div>

            {/* Detail Tab Switcher */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-4">
              <button onClick={() => setDetailTab("results")} className={`flex-1 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${detailTab === "results" ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                <i className="ri-bar-chart-line mr-1"></i>投票结果
              </button>
              <button onClick={() => setDetailTab("participants")} className={`flex-1 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${detailTab === "participants" ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                <i className="ri-group-line mr-1"></i>参与名单
                {selectedVote.participants && selectedVote.participants.length > 0 && <span className="ml-1 font-bold text-gray-400">{selectedVote.participants.length}</span>}
              </button>
            </div>
            {selectedVote.isAnonymous && detailTab === "participants" && (
              <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-2 mb-3 leading-relaxed">
                本投票为匿名投票：会员端不对外展示他人信息。以下记录仅后台留存，便于核对人数与选项，请勿对外泄露。
              </p>
            )}

            {/* Results Tab */}
            {detailTab === "results" && (
              <div>
                <button onClick={() => setReportVote(selectedVote)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-4 bg-gray-50 text-gray-600 rounded-xl hover:bg-red-50 hover:text-red-600 text-xs cursor-pointer transition-colors border border-gray-200 hover:border-red-200 whitespace-nowrap">
                  <i className="ri-bar-chart-2-line"></i> 导出投票结果报告
                </button>
                {(() => {
                  const total = totalVotes(selectedVote);
                  const hasImg = selectedVote.options.some((o) => o.imageUrl);
                  return selectedVote.options.map((opt, i) => {
                    const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
                    return (
                      <div key={opt.id} className="mb-3">
                        {hasImg && (
                          <div className="flex items-center gap-2 mb-1.5">
                            {opt.imageUrl ? <div className="w-10 h-7 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100"><img src={opt.imageUrl} alt={opt.text} className="w-full h-full object-cover" /></div>
                              : <div className="w-10 h-7 rounded-lg bg-gray-100 flex-shrink-0"></div>}
                            <span className="text-xs text-gray-600 flex-1 truncate">{opt.text}</span>
                            <span className="text-xs font-semibold text-gray-700 flex-shrink-0">{opt.votes}票</span>
                          </div>
                        )}
                        {!hasImg && (
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-gray-600 flex items-center gap-1.5">
                              <span className="w-4 h-4 flex items-center justify-center bg-gray-200 rounded-full text-gray-500 font-medium text-xs flex-shrink-0">{i + 1}</span>
                              {opt.text}
                            </span>
                            <span className="font-medium text-gray-700 flex-shrink-0 ml-2">{opt.votes}票 {pct}%</span>
                          </div>
                        )}
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-red-500 to-red-400 transition-all" style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    );
                  });
                })()}
                {totalVotes(selectedVote) === 0 && <p className="text-xs text-gray-400 text-center py-4">暂无投票数据</p>}
              </div>
            )}

            {/* Participants Tab（匿名投票仅在会员端隐藏他人信息，后台仍展示留存记录） */}
            {detailTab === "participants" && (
              <div>
                {selectedVote.participants && selectedVote.participants.length > 0 ? (
                  <>
                    <button
                      onClick={() => handleExportParticipants(selectedVote)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 bg-teal-50 text-teal-700 rounded-xl hover:bg-teal-100 text-xs cursor-pointer transition-colors border border-teal-100 whitespace-nowrap"
                    >
                      <i className="ri-download-line"></i> 导出参与名单 CSV
                    </button>
                    <div className="space-y-2">
                      {selectedVote.participants.map((p) => {
                        const avatarColors = ["bg-red-100 text-red-600", "bg-amber-100 text-amber-700", "bg-teal-100 text-teal-600", "bg-indigo-100 text-indigo-600", "bg-pink-100 text-pink-600"];
                        const colorIdx = p.memberId.charCodeAt(p.memberId.length - 1) % avatarColors.length;
                        const votedTexts = p.votedOptions.map((oid) => selectedVote.options.find((o) => o.id === oid)?.text ?? oid);
                        return (
                          <div key={p.memberId} className="flex items-start gap-2.5 p-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold ${avatarColors[colorIdx]}`}>{p.name.slice(0, 1)}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-gray-800">{p.name}</span>
                                <span className="text-[10px] text-gray-400 flex-shrink-0 ml-1">{p.time.slice(5)}</span>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {votedTexts.map((txt, i) => (
                                  <span key={i} className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded-md leading-tight max-w-[140px] truncate">{txt}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <i className="ri-group-line text-3xl text-gray-200 block mb-2"></i>
                    <p className="text-xs">暂无参与记录</p>
                  </div>
                )}
                <p className="text-[10px] text-gray-400 text-center mt-3 pt-3 border-t border-gray-50">仅展示近期 {selectedVote.participants?.length ?? 0} 条记录，完整名单请导出查看</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Member Preview Modal ── */}
      {showMemberPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[420px] max-h-[90vh] overflow-y-auto">
            <div className="bg-red-600 rounded-t-2xl px-5 py-4 flex items-center gap-3">
              <button onClick={() => setShowMemberPreview(null)} className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-white cursor-pointer rounded-lg hover:bg-white/10"><i className="ri-close-line"></i></button>
              <span className="text-white text-sm font-medium flex-1">会员端视角预览</span>
              <span className="text-white/60 text-xs">模拟</span>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-full flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span> 投票进行中</span>
                {showMemberPreview.isAnonymous && <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">匿名</span>}
              </div>
              <h4 className="font-bold text-gray-900 text-base mb-2">{showMemberPreview.title}</h4>
              {showMemberPreview.description && <p className="text-sm text-gray-500 mb-4 leading-relaxed">{showMemberPreview.description}</p>}
              <p className="text-xs text-gray-400 mb-4">截止时间：{showMemberPreview.endDate}{showMemberPreview.allowMultiple && " · 可多选"}</p>

              {showMemberPreview.options.some((o) => o.imageUrl) ? (
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {showMemberPreview.options.map((opt) => {
                    const isSelected = previewSelected.includes(opt.id);
                    return (
                      <button key={opt.id} onClick={() => { if (showMemberPreview.allowMultiple) { setPreviewSelected((p) => isSelected ? p.filter((id) => id !== opt.id) : [...p, opt.id]); } else { setPreviewSelected([opt.id]); } }} className={`text-left rounded-2xl border-2 overflow-hidden cursor-pointer transition-all ${isSelected ? "border-red-500" : "border-gray-100 hover:border-gray-300"}`}>
                        {opt.imageUrl ? <div className="h-24 overflow-hidden"><img src={opt.imageUrl} alt={opt.text} className="w-full h-full object-cover" /></div> : <div className="h-24 bg-gray-100 flex items-center justify-center"><i className="ri-image-line text-2xl text-gray-300"></i></div>}
                        <div className={`px-3 py-2.5 transition-colors ${isSelected ? "bg-red-50" : "bg-white"}`}>
                          <div className="flex items-start gap-2">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${isSelected ? "border-red-500 bg-red-500" : "border-gray-300"}`}>
                              {isSelected && <i className="ri-check-line text-white text-xs" style={{ fontSize: "8px" }}></i>}
                            </div>
                            <div className="min-w-0">
                              <p className={`text-xs font-semibold leading-tight truncate ${isSelected ? "text-red-700" : "text-gray-800"}`}>{opt.text}</p>
                              {opt.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">{opt.description}</p>}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-2 mb-5">
                  {showMemberPreview.options.map((opt) => {
                    const isSelected = previewSelected.includes(opt.id);
                    return (
                      <button key={opt.id} onClick={() => { if (showMemberPreview.allowMultiple) { setPreviewSelected((p) => isSelected ? p.filter((id) => id !== opt.id) : [...p, opt.id]); } else { setPreviewSelected([opt.id]); } }} className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all cursor-pointer text-sm ${isSelected ? "border-red-500 bg-red-50 text-red-700 font-medium" : "border-gray-100 bg-gray-50 text-gray-700 hover:border-gray-300"}`}>
                        <span className={`inline-flex w-5 h-5 items-center justify-center mr-2 rounded-full border-2 text-xs ${isSelected ? "border-red-500 bg-red-500 text-white" : "border-gray-300"}`}>{isSelected && <i className="ri-check-line text-xs"></i>}</span>
                        {opt.text}
                      </button>
                    );
                  })}
                </div>
              )}
              <button disabled={previewSelected.length === 0} className="w-full py-3 bg-red-600 text-white rounded-xl text-sm font-medium disabled:opacity-40 cursor-pointer whitespace-nowrap">提交投票</button>
              <p className="text-xs text-gray-400 text-center mt-3">此为管理员预览，实际投票由会员在前台参与</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Schedule Modal ── */}
      {scheduleVoteId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-6 w-[400px]">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-alarm-line text-amber-500 text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-1">设置定时发布</h3>
                <p className="text-sm text-gray-500 leading-relaxed">到达指定时间后，投票将<span className="text-green-600 font-medium">自动发布</span>并对会员可见，无需手动操作。</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-xs text-gray-600 mb-4 flex items-center gap-2">
              <i className="ri-information-line text-gray-400"></i>
              {votes.find((v) => v.id === scheduleVoteId)?.title ?? ""}
            </div>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-gray-600 mb-2">发布时间 *</label>
              <input
                type="datetime-local"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 cursor-pointer"
              />
              <p className="text-xs text-gray-400 mt-1.5">每隔 30 秒系统自动检查，到达时间即触发发布</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setScheduleVoteId(null); setScheduleTime(""); }} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button
                onClick={() => scheduleTime && handleSchedule(scheduleVoteId, scheduleTime)}
                disabled={!scheduleTime}
                className="flex-1 py-2.5 text-sm text-white bg-amber-500 rounded-xl hover:bg-amber-600 cursor-pointer disabled:opacity-50 whitespace-nowrap"
              >
                <i className="ri-alarm-line mr-1.5"></i> 确认定时
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-6 w-[380px]">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-delete-bin-line text-red-500 text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-1">确认删除此投票？</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  删除后<span className="text-red-600 font-medium">无法恢复</span>，包括所有选项配置。
                  <span className="block mt-1 text-xs text-amber-600">注：仅草稿状态的投票可以被删除。</span>
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 mb-5 text-xs text-gray-600">
              <i className="ri-information-line text-gray-400 mr-1"></i>
              {votes.find((v) => v.id === deleteConfirmId)?.title ?? ""}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDeleteConfirmed(deleteConfirmId)} className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap">
                <i className="ri-delete-bin-line mr-1.5"></i> 确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Report Modal ── */}
      {reportVote && <VoteReportModal vote={reportVote} onClose={() => setReportVote(null)} />}

      {/* ── Create Modal ── */}
      {showCreate && (
        <VoteCreateModal onSave={handleCreate} onClose={() => setShowCreate(false)} audience={audience} audienceLoading={audienceLoading} />
      )}

      {/* ── Edit Modal ── */}
      {editVote && (
        <VoteCreateModal
          initialVote={editVote}
          onSave={handleEditSave}
          onClose={() => setEditVote(null)}
          audience={audience}
          audienceLoading={audienceLoading}
        />
      )}
    </div>
  );
}
